import React, { useState, useEffect } from 'react';
import { db } from '../services/databaseService';
import { AIPrompt } from '../types';

export const AdminPromptsView: React.FC = () => {
  const [prompts, setPrompts] = useState<AIPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPrompt, setEditingPrompt] = useState<AIPrompt | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const data = await db.getPrompts();
      setPrompts(data);
    } catch (error) {
      console.error('Error fetching prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingPrompt) return;
    setIsSaving(true);
    try {
      if (editingPrompt.id) {
        await db.updatePrompt(editingPrompt.id, editingPrompt);
      } else {
        await db.addPrompt({
          name: editingPrompt.name,
          description: editingPrompt.description,
          system_instruction: editingPrompt.system_instruction,
          is_default: editingPrompt.is_default
        });
      }
      await fetchPrompts();
      setEditingPrompt(null);
    } catch (error) {
      console.error('Error saving prompt:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Ești sigur că vrei să ștergi acest prompt?')) return;
    try {
      await db.deletePrompt(id);
      await fetchPrompts();
    } catch (error) {
      console.error('Error deleting prompt:', error);
    }
  };

  const handleAddNew = () => {
    setEditingPrompt({
      id: '',
      name: '',
      description: '',
      system_instruction: '',
      is_default: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
  };

  if (loading) {
    return <div className="text-white p-6">Se încarcă prompt-urile...</div>;
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8 max-w-5xl">
      <div className="flex justify-between items-end">
        <div>
          <h3 className="text-white text-3xl font-black italic tracking-tight">AI Prompting</h3>
          <p className="text-indigo-400 text-[10px] font-black uppercase tracking-widest mt-2 italic">Gestionare comportament AI</p>
        </div>
        <button 
          onClick={handleAddNew}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all active:scale-95"
        >
          + Adaugă Prompt Nou
        </button>
      </div>

      {editingPrompt && (
        <div className="bg-white/5 p-6 md:p-8 rounded-[2rem] border border-white/10 space-y-4">
          <h4 className="text-white font-bold mb-4">{editingPrompt.id ? 'Editează Prompt' : 'Prompt Nou'}</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-indigo-300 uppercase tracking-widest">Nume (Cod intern)</label>
              <input 
                type="text" 
                value={editingPrompt.name}
                onChange={e => setEditingPrompt({...editingPrompt, name: e.target.value})}
                className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-indigo-500"
                placeholder="ex: live_assistant_default"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-indigo-300 uppercase tracking-widest">Descriere</label>
              <input 
                type="text" 
                value={editingPrompt.description || ''}
                onChange={e => setEditingPrompt({...editingPrompt, description: e.target.value})}
                className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-indigo-500"
                placeholder="Descriere scurtă"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-indigo-300 uppercase tracking-widest">Text Prompt (Instrucțiuni System)</label>
            <textarea 
              value={editingPrompt.system_instruction}
              onChange={e => setEditingPrompt({...editingPrompt, system_instruction: e.target.value})}
              className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-white text-sm outline-none focus:border-indigo-500 h-64 font-mono"
              placeholder="Ești un asistent legal..."
            />
            <p className="text-[10px] text-white/40">Variabile disponibile (în funcție de implementare): {`{USER_NAME}`}, {`{EVENTS}`}, {`{CASES}`}, {`{DOCS}`}</p>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <input 
              type="checkbox" 
              id="isActive"
              checked={editingPrompt.is_default}
              onChange={e => setEditingPrompt({...editingPrompt, is_default: e.target.checked})}
              className="w-4 h-4 rounded border-white/10 bg-black/40 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-black"
            />
            <label htmlFor="isActive" className="text-white text-sm">Activ</label>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-white/5">
            <button 
              onClick={() => setEditingPrompt(null)}
              className="px-6 py-2 rounded-xl text-[10px] font-black text-white/50 hover:text-white uppercase tracking-widest transition-all"
            >
              Anulează
            </button>
            <button 
              onClick={handleSave}
              disabled={isSaving}
              className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-6 py-2 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all"
            >
              {isSaving ? 'Se salvează...' : 'Salvează Prompt'}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {prompts.map(prompt => (
          <div key={prompt.id} className="bg-black/40 border border-white/5 rounded-[2rem] p-6 hover:bg-white/5 transition-colors group">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-3">
                  <h4 className="text-lg font-black text-white italic tracking-tight">{prompt.name}</h4>
                  <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest ${prompt.is_default ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                    {prompt.is_default ? 'Activ' : 'Inactiv'}
                  </span>
                </div>
                <p className="text-xs text-white/50 mt-1">{prompt.description}</p>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => setEditingPrompt(prompt)}
                  className="bg-white/5 hover:bg-white/10 text-white px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all"
                >
                  Editează
                </button>
                <button 
                  onClick={() => prompt.id && handleDelete(prompt.id)}
                  className="bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all"
                >
                  Șterge
                </button>
              </div>
            </div>
            <div className="bg-black/50 rounded-xl p-4 border border-white/5 max-h-32 overflow-y-auto">
              <pre className="text-[10px] text-indigo-200/70 whitespace-pre-wrap font-mono">
                {prompt.system_instruction}
              </pre>
            </div>
          </div>
        ))}
        {prompts.length === 0 && !editingPrompt && (
          <div className="text-center py-20 border border-white/5 rounded-[2rem] bg-black/20">
            <p className="text-white/40 italic">Niciun prompt definit.</p>
          </div>
        )}
      </div>
    </div>
  );
};
